// ---------- dictionnaire allemand ----------
// Copie conforme de fr.ts, clé pour clé — typé `Record<I18nKey, string>` ci-dessous, donc une clé
// manquante ou en trop est une ERREUR DE COMPILATION, jamais un texte français qui atteindrait la
// production. Voir fr.ts pour les conventions suivies ici (clés plates, `{{var}}` portant la
// phrase entière, les trois registres de shared/ traités à part dans ./index.ts).
//
// Tutoiement (« du ») et non « Sie » : c'est la norme des jeux en allemand, et c'est le registre
// que tiennent déjà le français et l'anglais.
//
// L'allemand est la langue qui ALLONGE le plus — compter un tiers de plus que le français sur un
// libellé de bouton. Les valeurs ci-dessous sont écourtées là où elles atterrissent dans une
// place contrainte (les onglets du bas, les pastilles de filtre, les deux lignes du splash),
// jamais dans les phrases qui ont de la place.
import type { I18nKey } from './fr';

const de: Record<I18nKey, string> = {
  // ---------- marque (index.html, p.html, splash.ts) ----------
  'brand.name': 'Codigrid',

  // ---------- index.html <head> ----------
  'app.title': 'Codigrid — Musterraster',
  'app.description':
    'Ein logisches Ausmalspiel: Aus einem Bild wird ein Raster aus verschlüsselten Zeichen.',

  // ---------- commun (plusieurs dialogues) ----------
  'common.close': 'Schließen',
  'common.cancel': 'Abbrechen',
  'common.back': 'Zurück',

  // ---------- home.ts / index.html #home ----------
  'home.filter.doing': 'Angefangen',
  'home.filter.done': 'Fertig',
  'home.tab.feed': 'Mein Feed',
  'home.tab.daily': 'Täglich',
  'home.tab.library': 'Bibliothek',
  'home.tab.settings': 'Einstellungen',
  'home.tab.create': 'Erstellen',
  'home.tab.new': 'Neu',
  'home.category.celebrite': 'Promis',
  'home.category.personnes': 'Porträts',
  'home.category.monuments': 'Bauwerke',
  'home.category.scenes': 'Alltagsszenen',
  'home.category.contes': 'Märchen und Sagen',
  'home.category.saisonnier': 'Saisonal',
  'home.category.paysage': 'Landschaft',
  'home.category.animaux': 'Tiere',
  'home.category.vehicules': 'Fahrzeuge',
  'home.category.sport': 'Sport',
  'home.category.art': 'Kunst',
  'home.category.divers': 'Sonstiges',
  'home.category.other': 'Andere',
  'home.facet.category.title': 'Kategorie',
  'home.facet.category.all': 'Alle',
  'home.facet.difficulty.title': 'Schwierigkeit',
  'home.facet.difficulty.all': 'Alle',
  'home.facet.alphabet.title': 'Alphabet',
  'home.facet.alphabet.all': 'Alle',
  // « Felder » reste invariable ici, comme en français : la plus petite grille fait 4 × 4, donc
  // le singulier ne s'affiche jamais.
  'home.card.tooltip': '{{w}} × {{h}} Felder · {{diff}}',
  'home.card.new': 'Neu',
  // ---------- die auf einer Karte angezeigte Dauer (home.ts, shared/effort.ts) ----------
  'home.card.eta': '≈ {{d}}',
  'home.card.etaLong': 'etwa {{d}} insgesamt',
  'home.card.leftLong': 'noch etwa {{d}}',
  'time.unit.min': 'Min.',
  'time.unit.h': 'Std.',
  'time.min': '{{n}} Min.',
  'time.h': '{{n}} Std.',
  'time.hmin': '{{h}} Std. {{m}}',
  'time.hplus': '{{n}} Std.+',
  'home.empty.noMatch': 'Noch kein Rätsel passt zu diesen Filtern.',
  'home.empty.resetFilters': 'Filter zurücksetzen',
  'home.empty.libraryDone':
    'Du hast jedes verfügbare Rätsel angefangen. Schau wieder rein — es kommen neue.',
  'home.empty.libraryDonePacks':
    'Du hast alle deine Rätsel angefangen. Neue Pakete warten auf dich.',
  'home.empty.seePacks': 'Pakete ansehen',
  'home.empty.puzzleGone': 'Dieses Rätsel ist nicht verfügbar.',
  'home.empty.retry': 'Nochmal versuchen',
  'home.empty.noneDoing': 'Kein Rätsel angefangen. Such dir eins in der Bibliothek aus.',
  'home.empty.openLibrary': 'Bibliothek öffnen',
  'home.empty.noneDone': 'Noch nichts fertig. Kommt schon.',
  'home.empty.noPuzzles': 'Noch kein Rätsel verfügbar.',
  'home.empty.serverError': 'Der Server war nicht erreichbar.',
  'home.loading': 'Lädt…',
  // ---------- die Bewertungskarte im Feed (home.ts::rateCard, Regeln in review.ts) ----------
  'home.rate.1.title': 'Du kennst Codigrid jetzt gut',
  'home.rate.1.body':
    'Deine Bewertung im Play Store hilft anderen Ausmal-Fans zu entscheiden, ob das Spiel etwas für sie ist.',
  'home.rate.1.cta': 'Meine Meinung teilen',
  'home.rate.2.title': 'Hast du Spaß mit Codigrid?',
  'home.rate.2.body':
    'Sag es in 10 Sekunden im Play Store – für ein kleines Spiel macht das wirklich viel aus.',
  'home.rate.2.cta': 'Codigrid bewerten',
  'home.rate.3.title': 'Lust auf neue Rätsel?',
  'home.rate.3.body': 'Jede Bewertung im Play Store zählt: Sie hilft mir, weitere zu erstellen.',
  'home.rate.3.cta': 'Bewertung schreiben',
  'home.progression.open': 'Fortschritt',
  'home.packsBanner.sub': '{{n}} Pakete zum Freischalten',
  'home.packsBanner.one': '1 Paket zum Freischalten',
  'home.packsBanner.cta': 'Entdecken',

  // ---------- Kreationen: home.ts (Karte, Filter, Karten) und creer.ts (das Werkzeug) ----------
  'home.create.title': 'Mein Rätsel erstellen',
  'home.create.sub': 'Aus einem deiner Fotos',
  'home.facet.mine': 'Meine Kreationen',
  'home.pack.base': 'Für den Einstieg',
  'home.card.mine': 'Meine Kreation',
  'home.empty.noCreations': 'Du hast noch kein Rätsel erstellt. Deine Kreationen erscheinen hier.',
  'home.creation.delete': 'Diese Kreation löschen',
  'home.creation.deleteConfirm':
    '„{{title}}“ löschen? Das Rätsel und dein Fortschritt darin gehen verloren.',
  'creer.step': '{{n}} / {{total}}',
  'creer.next': 'Weiter',
  'creer.busy': 'Das Blatt wird vorbereitet…',
  'creer.photo.title': 'Dein Foto',
  'creer.photo.camera': 'Foto aufnehmen',
  'creer.photo.gallery': 'Meine Galerie',
  'creer.photo.tipsTitle': 'Die Fotos, die die schönsten Rätsel ergeben',
  'creer.photo.tip1': 'Ein Motiv, das den Rahmen füllt',
  'creer.photo.tip1Sub': 'Ein Gesicht, ein Tier, eine Silhouette, aus der Nähe.',
  'creer.photo.tip2': 'Kontrast',
  'creer.photo.tip2Sub': 'Deutliche Hell- und Dunkeltöne: Sie zeichnen das Bild.',
  'creer.photo.tip3': 'Ein scharfes Foto bei gutem Licht',
  'creer.photo.tip3Sub': 'Tageslicht, kein Gegenlicht, keine Unschärfe.',
  'creer.photo.tip4': 'Keine winzigen Details',
  'creer.photo.tip4Sub':
    'Text, eine Menschenmenge oder eine ferne Landschaft gehen in den Feldern verloren.',
  'creer.photo.cutTitle': 'Ein Porträt? Entferne zuerst den Hintergrund',
  'creer.photo.cutText':
    'Mit einem Werkzeug wie zum Beispiel {{link}}, dann wähle das Bild in Meine Galerie: Der Hintergrund wird zu Papier.',
  'creer.photo.private':
    '<b>Dein Foto wird nie gesendet.</b> Das Raster entsteht auf deinem Handy, und nur du siehst das Rätsel.',
  'creer.photo.error': 'Dieses Bild lässt sich nicht lesen. Versuch es mit einem anderen Foto.',
  'creer.crop.title': 'Zuschneiden',
  'creer.crop.hint': 'Zum Zoomen zusammenziehen, zum Verschieben ziehen',
  'creer.crop.photo': 'Foto',
  'creer.crop.square': 'Quadrat',
  'creer.crop.portrait': 'Hochformat',
  'creer.crop.landscape': 'Querformat',
  'creer.crop.rotate': 'Um eine Vierteldrehung drehen',
  'creer.adjust.title': 'Dein Blatt',
  'creer.tool.tech': 'Technik',
  'creer.tool.detail': 'Details',
  'creer.tool.light': 'Licht',
  'creer.light.normal': 'Normal',
  'creer.light.clear': 'Hell',
  'creer.light.notch': 'Licht {{n}} von {{total}}',
  'creer.tool.contrast': 'Kontrast',
  'creer.contrast.soft': 'Natürlich',
  'creer.contrast.strong': 'Kräftig',
  'creer.contrast.notch': 'Kontrast {{n}} von {{total}}',
  'creer.zoomReset': 'Ganze Vorschau zeigen',
  'creer.detail.cells': 'Anzahl der Felder',
  'creer.detail.readout': '{{w}} × {{h}} · {{n}} Felder',
  'creer.detail.notch': '{{n}} Felder pro Seite',
  'creer.detail.less': 'Schnell gemalt',
  'creer.detail.more': 'Viele Details',
  'creer.ready.title': 'Zusammenfassung',
  'creer.ready.name': 'Sein Name',
  'creer.ready.placeholder': 'Mein Rätsel',
  'creer.ready.photo':
    '<b>Dein Foto bleibt auf deinem Handy.</b> Es wird nie gesendet, und das Rätsel daraus auch nicht.',
  'creer.ready.private':
    '<b>Dieses Rätsel gehört nur dir, auf diesem Handy.</b> Es wird nirgends sonst gespeichert: Deinstallierst du das Spiel oder wechselst das Handy, kommt es nicht mit.',
  'creer.ready.go': 'Rätsel speichern',
  'creer.ready.saving': 'Wird gespeichert…',
  'creer.ready.storeFailed':
    'Das Rätsel konnte auf diesem Handy nicht gespeichert werden. Mach etwas Speicher frei und versuch es noch einmal.',
  'creer.ready.tooMany':
    'Du hast schon {{n}} Kreationen, das Maximum. Lösch eine unter „Meine Kreationen“, um eine neue zu machen.',
  'creer.ready.full':
    'Rätsel erstellen ist im Moment nicht möglich. Versuch es später noch einmal.',
  'creer.ready.freeLeftMany': 'Noch {{n}} Kreationen gratis, ohne Werbung.',
  'creer.ready.freeLeftOne': 'Noch 1 Kreation gratis, ohne Werbung.',
  'creer.ready.adNote':
    'Deine erste Kreation war gratis. Diese wird nach einer kurzen Werbung gespeichert – davon lebt das Spiel.',
  'creer.ready.goAd': 'Werbung ansehen und speichern',
  'creer.ready.adSkipped':
    'Werbung abgebrochen: Deine Kreation ist noch nicht gespeichert. Versuch es jederzeit noch einmal.',
  'creer.ready.adUnavailable': 'Gerade ist keine Werbung verfügbar. Versuch es gleich noch einmal.',

  // ---------- daily.ts / index.html #dailyPage (das Rätsel des Tages) ----------
  'daily.month.prev': 'Voriger Monat',
  'daily.month.next': 'Nächster Monat',
  'daily.state.todo': 'Zu malen',
  'daily.state.doing': 'Angefangen, {{pct}} %',
  'daily.state.done': 'Fertig',
  'daily.cell.aria': '{{date}} — {{state}}',
  'daily.empty.stock': 'Heute gibt es kein Rätsel. Komm morgen wieder.',
  'daily.empty.update': 'Dieses Rätsel braucht eine neuere Version der App.',
  'daily.today.kicker': 'Das Blatt von heute',
  'daily.today.unknown': 'Noch weiß niemand, was es ist',
  'daily.today.paint': 'Ausmalen',
  'daily.today.resume': 'Weitermalen',
  'daily.today.see': 'Nochmal ansehen',

  // ---------- packs.ts / index.html #packs (freischaltbare Rätselpakete) ----------
  'packs.title': 'Verfügbare Pakete',
  'packs.carousel': 'Karussell',
  'packs.loading': 'Lädt…',
  'packs.empty': 'Noch nichts zum Freischalten. Schau wieder rein — es kommt mehr.',
  'packs.puzzlesLabel.one': 'Rätsel',
  'packs.puzzlesLabel.other': 'Rätsel',
  'packs.selection': 'Auswahl',
  'packs.unlock': 'Freischalten',
  'packs.watchAd': 'Werbung ansehen',
  // ---------- les arguments d'un pack (src/packArgument.ts) ----------
  'packs.arg.debutant': 'Gut zum Einsteigen',
  'packs.arg.portraits': 'Gesichter',
  'packs.arg.patience': 'Lass dir Zeit',
  'packs.arg.nouveau': 'Ganz neu',
  'packs.arg.costaud': 'Große Raster',
  'packs.arg.express': 'Kurze Runden',
  'packs.arg.mono': 'Eine Technik',
  'packs.arg.varie': 'Gemischte Techniken',
  'packs.arg.couleur': 'In Farbe',
  'packs.unlocking': 'Wird freigeschaltet…',
  // Sous le bouton Freischalten : dit qu'il n'y a pas d'argent en jeu — cf. src/packs.ts,
  // unlockNote(). Le `<b>` est du balisage voulu, même précédent que home.intro.
  'packs.adNote': '<b>Kostenlos</b> · etwa 30 s',
  'packs.freeNote': '<b>Kostenlos</b>, ein Tipp',
  'packs.adLoading': 'Werbung lädt…',
  'packs.adUnavailable': 'Keine Werbung verfügbar — versuch es nochmal.',
  'packs.adSkipped': 'Werbung nicht zu Ende gesehen.',
  'packs.adFailed': 'Hat nicht geklappt — versuch es nochmal.',
  'packs.reveal.title.one': '{{n}} Rätsel freigeschaltet!',
  'packs.reveal.title.other': '{{n}} Rätsel freigeschaltet!',
  'packs.reveal.sub': '„{{title}}“ ist jetzt in deiner Bibliothek',
  'packs.reveal.continue': 'Weiter',
  'packs.feature.coeur': 'Unser Favorit',
  'packs.feature.saison': 'Zur Saison',
  'packs.feature.nouveau': 'Neu',
  'packs.feature.evenement': 'Besonderes Ereignis',
  'packs.feature.joueurs': 'Liebling der Spieler',
  'packs.feature.populaire': 'Am häufigsten freigeschaltet',
  'packs.volume': '{{title}} Nr. {{n}}',
  'packs.intro': '{{n}} Rätsel zu entdecken',
  'packs.shelf.n': '{{n}} Pakete',
  'packs.unlocks': 'Von {{n}} Spielern freigeschaltet',
  'packs.fact.time': 'zum Ausmalen',
  'packs.fact.votes': '{{n}} Bewertungen',
  'packs.fact.unrated': 'noch nicht bewertet',
  'packs.inside': 'Was drin ist',
  'packs.inside.hint': 'Jedes Motiv zeigt sich erst beim Ausmalen.',
  'packs.sheet.puzzle': 'Rätsel {{n}}',
  'technique.family.motifs': 'Muster',
  'technique.family.hachures': 'Schraffur',
  'technique.family.ascii': 'ASCII',
  'technique.family.pointille': 'Punktierung',
  'technique.family.des': 'Würfel',
  'technique.family.briques': 'Bausteine',
  'technique.family.peinture': 'Malerei',
  'technique.family.croix': 'Stickerei',

  // ---------- progression.ts / index.html #progression (Abzeichen + Gemeinsam) ----------
  'progression.title': 'Fortschritt',
  'progression.tier.bronze': 'Bronze',
  'progression.tier.argent': 'Silber',
  'progression.tier.or': 'Gold',
  'progression.tier.platine': 'Platin',
  'progression.loading': 'Lädt…',
  'progression.needAccount': 'Löse ein Rätsel, um zu sehen, was die anderen malen.',
  'progression.loadFailed': 'Der Server war nicht erreichbar.',
  // L’écran « Moi » et ses badges (src/profile.ts) — les règles, seuils compris, viennent du serveur.
  'progression.tab.me': 'Ich',
  // L’onglet « Ensemble » (src/together.ts) et la rareté des badges — ce qui a remplacé la ligue.
  'progression.tab.together': 'Gemeinsam',
  'me.rarity': '{{pct}} % der Spieler',
  'me.rarity.rare': 'Selten · {{pct}} % der Spieler',
  'me.rarity.locked': 'Schon {{pct}} % der Spieler haben es',
  'together.eyebrow': 'Diese Woche · seit Montag',
  'together.sheets.one': 'Rätsel gelöst',
  'together.sheets.other': 'Rätsel gelöst',
  'together.players.one': 'von {{n}} Spieler',
  'together.players.other': 'von {{n}} Spielern',
  'together.stats': 'Die Woche in Zahlen',
  'together.trend.eyebrow': 'Trend',
  'together.trend.up': 'im Vergleich zur Vorwoche',
  'together.trend.same': 'ungefähr wie letzte Woche',
  'together.trend.calm': 'etwas ruhiger als letzte Woche',
  'together.trend.ctx': '{{n}} Rätsel, gegenüber {{last}} zur selben Zeit letzte Woche.',
  'together.peak.eyebrow': 'Hauptzeit',
  'together.peak.what': 'wann am meisten ausgemalt wird',
  'together.crossed.eyebrow': 'Abzeichen',
  'together.crossed.one': 'Stufe seit Montag erreicht',
  'together.crossed.other': 'Stufen seit Montag erreicht',
  'together.joined.eyebrow': 'Willkommen',
  'together.joined.one': 'neuer Spieler diese Woche',
  'together.joined.other': 'neue Spieler diese Woche',
  'together.joined.ctx': 'Letzte Woche zur selben Zeit: {{last}}.',
  'together.empty':
    'Diese Woche hat noch niemand ein Rätsel gelöst. Das erste ist vielleicht deins.',
  'together.mine.title': 'Dein Anteil diese Woche.',
  'together.mine.zero': 'Diese Woche noch kein Rätsel. Kein Grund zur Eile.',
  'together.mine.one': '1 von {{total}} Rätseln gelöst.',
  'together.mine.other': '{{n}} von {{total}} Rätseln gelöst.',
  'together.daily.one': '{{n}} Spieler hat das Tagesrätsel schon gemalt.',
  'together.daily.other': '{{n}} Spieler haben das Tagesrätsel schon gemalt.',
  'together.daily.with': 'Mit dir haben {{n}} Spieler das Tagesrätsel gemalt.',
  'together.daily.go': 'Malen',
  'progression.tier.prisme': 'Prisma',
  'me.eyebrow': 'Dein Spitzname',
  'me.edit': 'Spitznamen ändern',
  'me.choose': 'Wähle deinen Spitznamen',
  'me.since': 'Dabei seit {{date}}',
  'me.days.one': '{{n}} Tag',
  'me.days.other': '{{n}} Tage',
  'me.numbers': 'Deine Zahlen',
  'me.cells.eyebrow': 'Gemalte Felder',
  'me.cells.what': 'seit du dabei bist',
  'me.fav.eyebrow': 'Dein Stil',
  'me.fav.cat': 'Lieblingskategorie: {{cat}}',
  'me.biggest.eyebrow': 'Dein größtes Rätsel',
  'me.biggest.cells': '{{n}} gemalte Felder',
  'me.biggest.untitled': 'Ohne Titel',
  'me.activity.title': 'Deine letzten 12 Wochen',
  'me.activity.more': 'mehr',
  'me.activity.less': 'weniger',
  'me.activity.today': 'heute',
  'me.activity.ago': 'vor 12 Wochen',
  'me.activity.how': 'Ein Feld pro Tag: Je dunkler, desto mehr Rätsel hast du an dem Tag gelöst.',
  'me.activity.one': '1 Tag mit mindestens einem gelösten Rätsel',
  'me.activity.other': '{{n}} Tage mit mindestens einem gelösten Rätsel',
  'me.fav.none': 'Noch offen',
  'me.fav.noneHint': 'Löse ein Rätsel, um ihn zu entdecken',
  'me.activity.none': 'Noch kein gelöstes Rätsel: Jedes färbt das Feld seines Tages dunkler.',
  'together.cells.eyebrow': 'Gemalte Felder',
  'together.cells.what': 'von allen, seit Montag',
  'together.best.eyebrow': 'Bester Tag',
  'together.best.one': '{{n}} Rätsel gelöst',
  'together.best.other': '{{n}} Rätsel gelöst',
  'together.top.eyebrow': 'Am besten bewertet diese Woche',
  'together.top.votes.one': '{{n}} Bewertung',
  'together.top.votes.other': '{{n}} Bewertungen',
  'together.top.done': 'Schon gemalt',
  'together.top.locked': 'In einem Paket zum Freischalten',
  'together.top.unlock': 'Paket freischalten',
  'me.stat.finished': 'Rätsel gelöst',
  'me.stat.daily': 'Tagesrätsel',
  'me.stat.styles': 'Stile gespielt',
  'me.tally': 'Stufen freigeschaltet',
  'me.section.milestones': 'Meilensteine',
  'me.section.families': 'Alphabete',
  'me.section.categories': 'Kategorien',
  'me.empty': 'Mach ein Rätsel fertig, um deine ersten Abzeichen freizuschalten.',
  'badge.library': 'Bibliothek',
  'badge.daily': 'Tagesrätsel',
  'badge.share': 'Teilen',
  'badge.family.dots': 'Punktierung',
  'badge.family.shapes': 'Formen',
  'badge.family.hatching': 'Schraffur',
  'badge.family.dice': 'Würfel',
  'badge.family.ascii': 'ASCII',
  'badge.family.bricks': 'Bausteine',
  'badge.family.paint': 'Malerei',
  'badge.family.embroidery': 'Kreuzstich',
  'badge.goal.library':
    'Mach Rätsel aus der Bibliothek fertig. Tagesrätsel haben ihr eigenes Abzeichen.',
  'badge.goal.daily':
    'Mach Tagesrätsel fertig. Keine Serie nötig: Ein über den Kalender nachgeholter Tag zählt auch.',
  'badge.goal.share':
    'Teile deine fertigen Bilder. Jedes Blatt zählt nur einmal, auch wenn du es mehrmals teilst.',
  'badge.goal.family': 'Mach Rätsel in {{alphabets}} fertig.',
  'badge.goal.category': 'Mach Rätsel aus der Kategorie {{category}} fertig.',
  'badge.unit.puzzles.one': '{{n}} Rätsel',
  'badge.unit.puzzles.other': '{{n}} Rätsel',
  'badge.unit.shares.one': '{{n}} Blatt',
  'badge.unit.shares.other': '{{n}} Blätter',
  'badge.next.bronze': 'bis Bronze',
  'badge.next.argent': 'bis Silber',
  'badge.next.or': 'bis Gold',
  'badge.next.platine': 'bis Platin',
  'badge.next.prisme': 'bis Prisma',
  'badge.reached': 'Stufe {{tier}} erreicht',
  'badge.earned': 'Erreicht',
  'badge.locked': 'Noch gesperrt',

  'celebrate.eyebrow': 'Neue Stufe',
  'celebrate.next': 'Weiter',
  'celebrate.later': 'Später',
  'celebrate.see': 'Meine Abzeichen',
  'celebrate.skipped.one': '{{tiers}} gleich mit erreicht',
  'celebrate.skipped.other': '{{tiers}} gleich mit erreicht',
  'celebrate.more.eyebrow': 'Und dazu',
  'celebrate.more.title.one': '{{n}} weitere Stufe',
  'celebrate.more.title.other': '{{n}} weitere Stufen',
  'celebrate.more.line': 'Du findest sie alle bei deinen Abzeichen.',
  'celebrate.welcome.eyebrow': 'Neu: Abzeichen',
  'celebrate.welcome.title': 'Deine Abzeichen sind da',
  'celebrate.welcome.count': 'Stufen schon erreicht',
  'celebrate.welcome.line': 'Sie zählen alles, was du bisher ausgemalt hast.',

  // ---------- prefs.ts / la page Réglages (index.html #prefsPage) ----------
  'prefs.lang.label': 'Sprache',
  'prefs.lang.hint': 'Ändert jeden Text in der App',
  'prefs.skin.label': 'Design',
  'prefs.skin.hint': 'Wie der Bildschirm aussieht — das Blatt bleibt, wie es ist',
  'prefs.sound.label': 'Ton',
  'prefs.sound.hint': 'Ein Ton pro gefülltem Feld',
  'prefs.haptics.label': 'Vibration',
  'prefs.haptics.hint': 'Ein Impuls in der Hand bei jedem Feld',
  'prefs.motion.label': 'Animationen',
  'prefs.motion.hint': 'Aufblühende Felder, Partikel, Erschütterungen',
  'prefs.compass.label': 'Kompass',
  'prefs.compass.hint': 'Ein Pfeil zum Zeichen, sobald es aus dem Bild läuft',
  'prefs.push.label': 'Mitteilungen',
  'prefs.push.hint': 'Neue Rätsel, Erinnerungen',
  'prefs.inks.label': 'Farbe der Felder',
  'prefs.inks.hint': 'Womit sich das Blatt füllt',
  'prefs.marks.label': 'Feldmarkierung',
  'prefs.marks.hint': 'Wie die noch offenen Felder markiert werden',
  'prefs.markHues.label': 'Farbe der Markierung',
  'prefs.markHues.hint': 'Betrifft nur die Markierung, nicht die Tinte',
  'prefs.username.label': 'Spitzname',
  'prefs.username.hint': 'Sichtbar, falls du ihn irgendwann zeigen willst',
  'prefs.username.placeholder': 'Spitzname aussuchen',
  'prefs.username.random': 'Spitznamen vorschlagen',
  'prefs.username.save': 'Speichern',
  'prefs.username.saving': 'Wird gespeichert…',
  'prefs.username.taken': 'Dieser Spitzname ist schon vergeben.',
  'prefs.username.rejected': 'Dieser Name ist nicht erlaubt. Wähle einen anderen.',
  'prefs.username.failed': 'Hat nicht geklappt — versuch es mit Verbindung nochmal.',
  // ---------- der Wiederherstellungscode (prefs.ts) ----------
  'prefs.recovery.label': 'Fortschritt zurückholen',
  'prefs.recovery.hint': 'Ein Code zum Aufbewahren, für ein anderes Handy',
  'prefs.recovery.mine':
    'Notier diesen Code irgendwo. Er holt deinen Fortschritt auf ein anderes Gerät zurück, oder nach einer Neuinstallation.',
  'prefs.recovery.loading': 'Einen Moment…',
  'prefs.recovery.offline': 'Code nicht verfügbar — versuch es mit Verbindung nochmal.',
  'prefs.recovery.copy': 'Kopieren',
  'prefs.recovery.copied': 'Kopiert',
  'prefs.recovery.theirs':
    'Du hast schon einen Code? Tipp ihn hier ein: dieses Gerät übernimmt diesen Fortschritt, und was du hier gemalt hast, bleibt.',
  'prefs.recovery.placeholder': 'die Wörter mit Bindestrichen',
  'prefs.recovery.use': 'Wiederherstellen',
  'prefs.recovery.restoring': 'Wird wiederhergestellt…',
  'prefs.recovery.unknown': 'Zu diesem Code gibt es keinen Fortschritt.',
  'prefs.recovery.failed': 'Hat nicht geklappt — versuch es mit Verbindung nochmal.',
  'prefs.recovery.done': 'Fortschritt zurückgeholt.',
  'maker.eyebrow': 'Ein Wort vom Macher',
  'maker.title': 'Hallo, ich bin tidou44',
  'maker.who':
    'Ich bin selbstständiger Webentwickler und habe Codigrid allein gebaut, aus Freude daran, wie ein Bild Feld für Feld entsteht.',
  'maker.ask':
    'Werbung bleibt bewusst zurückhaltend. Wenn dir das Spiel gefällt, hilft mir Teilen oder Bewerten sehr: Ich habe noch viele Ideen.',
  'maker.askWeb':
    'Werbung bleibt bewusst zurückhaltend. Wenn dir das Spiel gefällt, hilft mir Teilen sehr: Ich habe noch viele Ideen.',
  'maker.share': 'Spiel teilen',
  'maker.rate': 'Bewerten',
  'maker.contact': 'Schreib mir',
  'maker.shareText':
    'Probier Codigrid aus, ein logisches Ausmalspiel, bei dem das Raster nie verrät, was es verbirgt. Vorerst nur für Android verfügbar.',
  'prefs.ads.label': 'Werbeeinstellungen',
  'prefs.ads.hint': 'Deine Einwilligung in Werbung noch einmal ansehen',
  'prefs.privacy.label': 'Datenschutz',
  'prefs.privacy.hint': 'Was auf dem Gerät bleibt und was gesendet wird',
  'prefs.wipe.label': 'Meine Daten löschen',
  'prefs.wipe.hint': 'Das Konto und alle Spielstände, hier und auf dem Server',
  'prefs.wipe.confirm':
    'Konto und alle Spielstände löschen? Das lässt sich nicht rückgängig machen.',
  'prefs.wipe.progress': 'Wird gelöscht…',
  'prefs.wipe.failed':
    'Hat nicht geklappt — es wurde nichts gelöscht. Versuch es mit Verbindung nochmal.',

  // ---------- mark.ts (Feldmarkierung, nur im Spiel — nicht in shared/) ----------
  'mark.battant.label': 'Dicker Rand',
  'mark.battant.hint': 'Ein dicker Rand, die Zeichnung unberührt',
  'mark.hachures.label': 'Schraffur',
  'mark.hachures.hint': 'Streifen über den Feldern',
  'mark.cases.label': 'Kreuz pro Feld',
  'mark.cases.hint': 'Ein Kreuz in der Mitte jedes Feldes',
  'skin.clair': 'Hell',
  'skin.ciel': 'Himmel',
  'skin.soir': 'Abend',

  'mark.hue.citron': 'Limette',
  'mark.hue.ambre': 'Bernstein',
  'mark.hue.rose': 'Rosa',
  'mark.tooltip': '{{label}} — {{hint}}',
  'mark.ariaLabel': '{{label}}. {{hint}}',

  // ---------- coach.ts et gestureDialog.ts : ce que l'écran apprend au début ----------
  'coach.boussole': 'Das Zeichen geht dort weiter. Tippe auf den Pfeil.',
  'coach.close': 'Diesen Hinweis ausblenden',
  'ordre.later':
    'Die Farben kommen <b>der Reihe nach</b>: von hell nach dunkel, zum Schluss das Weiß für die Lichter. Diese kommt später.',
  'coach.hello.title': 'Hallo, willkommen!',
  'coach.hello.body':
    'Ich bin der Fuchs von Codigrid. Jedes Raster versteckt ein Bild: Füll seine Felder aus, und es erscheint.',
  'coach.hello.next': 'Zeig’s mir',
  'coach.dlg.title': 'Zwei Arten auszumalen',
  'coach.dlg.tap': '<b>Tippe</b> ein hervorgehobenes Feld an — sein ganzes Gebiet füllt sich.',
  'coach.dlg.swipe':
    '<b>Wische</b>, ohne den Finger zu heben — jedes überquerte Gebiet füllt sich.',
  'coach.dlg.next': 'Weiter',
  'coach.move.title': 'Im Bild unterwegs',
  'coach.move.drag':
    '<b>Zieh</b> das Raster, um dich zu bewegen – beginne auf einem Feld, das nicht hervorgehoben ist.',
  'coach.move.pinch':
    '<b>Mit zwei Fingern</b>: spreizen zum Vergrößern, zusammenziehen zum Verkleinern.',
  'coach.dlg.go': 'Los geht’s',
  'piece.done.title': 'Super!',
  'piece.done.body':
    'Das war nur sein Blick: Der Rest des Bildes wartet auf dich. Und es ist nicht das einzige.',
  'piece.done.packs': '<b>Hunderte Bilder</b> zum Herunterladen, in Pakete sortiert',
  'piece.done.daily': 'Ein <b>Bild des Tages</b>, im Tab Täglich',
  'piece.done.create': 'Deine <b>eigenen Puzzles</b>, aus deinen Fotos',
  'piece.done.ask': 'Malen wir dieses Bild zusammen fertig, oder willst du die Bibliothek sehen?',
  'piece.done.finish': 'Dieses Bild fertig malen',
  'piece.done.library': 'Zur Bibliothek',
  'intro.bigDone':
    'Dein erstes Bild ist fertig! Hunderte weitere warten in der Bibliothek auf dich.',
  'piece.bravo': 'Super!',
  'piece.line': 'Das war nur sein Blick. Der Rest wartet auf dich.',
  'prefs.intro.label': 'Einführung wiederholen',
  'prefs.intro.hint': 'Das erste Bild, mit dem Fuchs',
  'prefs.coach.label': 'Gesten noch einmal zeigen',
  'prefs.coach.hint': 'Ausmalen, zoomen, verschieben, als Animation',

  // ---------- dialogue de mise à jour (pwa.ts / index.html #update) ----------
  'update.title': 'Update verfügbar',
  'update.body':
    'Eine neue Version von Codigrid ist fertig. Sie wird beim Neuladen der Seite installiert — dein Fortschritt bleibt erhalten.',
  'update.reload': 'Neu laden',
  'update.reloading': 'Wird aktualisiert…',

  // ---------- écran de jeu (ui.ts / index.html .app) ----------
  'game.close': 'Blatt schließen',
  'game.finished': 'Fertig',
  'game.finishedSub': '{{todo}} Felder · {{w}}×{{h}} · {{alphabet}}',
  // La ligne « Avec toi, … » de la carte de fin (ui.ts, showPeers).
  'game.peers.sheet': 'Mit dir haben {{n}} Spieler dieses Bild fertig gemalt.',
  'game.peers.daily': 'Mit dir haben {{n}} Spieler das Tagesrätsel gemalt.',
  'game.peers.first': 'Du gehörst zu den Ersten, die dieses Bild fertig haben!',
  'game.peers.none': 'Vor dir hatte es noch niemand fertig!',
  'game.rate': 'Dieses Rätsel bewerten',
  'game.replay': 'Animation ansehen',
  'game.share': 'Teilen',
  'game.linkCopied': '✓ Link kopiert',
  'shareSheet.image': 'Bild',
  'shareSheet.imageSub': 'Die fertige Zeichnung',
  'shareSheet.video': 'Video',
  'shareSheet.videoSub': 'Das Ausmalen, in 10 Sekunden',
  'shareSheet.imageBusy': 'Das Bild wird vorbereitet…',
  'shareSheet.videoBusy': 'Dein Ausmalen wird gefilmt…',
  'shareSheet.imageReady': 'Dein Bild ist fertig!',
  'shareSheet.videoReady': 'Dein Video ist fertig!',
  'shareSheet.imageSend': 'Bild senden',
  'shareSheet.videoSend': 'Video senden',
  'shareSheet.imageSaved': '✓ Bild gespeichert',
  'shareSheet.videoSaved': '✓ Video gespeichert',
  'shareSheet.other': 'Etwas anderes wählen',
  'shareSheet.failed': 'Die Datei konnte nicht vorbereitet werden. Versuch es gleich noch einmal.',
  'shareSheet.msg.mineVideo':
    'Mein Foto wird Feld für Feld zur Zeichnung. Gemacht mit Codigrid, kostenlos für Android:\n{{url}}',
  'shareSheet.msg.mineImage':
    'Mein Foto, in Codigrid zum Ausmalbild geworden. Kostenlos für Android:\n{{url}}',
  'shareSheet.msg.video':
    'Schau, wie diese Zeichnung Feld für Feld entsteht. Codigrid, kostenlos für Android:\n{{url}}',
  'shareSheet.msg.image': 'Feld für Feld in Codigrid ausgemalt. Kostenlos für Android:\n{{url}}',
  'game.shareText':
    'Ich habe gerade ein Blatt in Codigrid fertig gemacht — ein logisches Ausmalspiel, bei dem das Raster nie verrät, was es verbirgt.',

  // ---------- splash.ts ----------
  // Longueur tenue près de l'original — cf. l'avertissement à côté de L1/L2 dans splash.ts :
  // fit() dimensionne le texte du splash sur le nombre exact de caractères de ces deux lignes.
  'splash.line1': 'Feld für Feld,',
  'splash.line2': 'das Bild erscheint',

  // ---------- report.ts (der Absturz beim Start) ----------
  'crash.boot': 'Das Spiel konnte nicht starten.',

  // ---------- share.ts (texte injecté dans p.html au chargement) ----------
  'share.title': '„{{title}}“, fertig',
  'share.meta': '{{w}} × {{h}} · {{alphabet}}',
  'share.docTitle': '{{title}} — Codigrid',

  // ---------- p.html (le squelette statique de la page de partage) ----------
  'share.page.metaDescription':
    'Codigrid, ein logisches Ausmalspiel: ein Raster aus verschlüsselten Zeichen und das Bild, das unter den Fingern entsteht.',
  'share.page.docTitleDefault': 'Ein fertiges Blatt — Codigrid',
  'share.page.title': 'Ein fertiges Blatt',
  'share.page.pitch':
    'Codigrid ist ein logisches Ausmalspiel. Jedes Feld trägt ein verschlüsseltes Zeichen, du füllst sie Zeichen für Zeichen, und das Bild entsteht unter deiner Hand. <em>Das Raster verrät nie, was es verbirgt.</em>',
  'share.page.play': 'Jetzt spielen',
  'share.page.store': 'Jetzt bei Google Play',
  'share.page.fine': 'Kostenlos, ohne Konto. Jeden Tag ein neues Blatt.',
  'share.page.fail': 'Diese Zeichnung ist nicht mehr verfügbar.',
  'share.page.again': '↻ Füllung noch einmal ansehen',
};

export default de;
